import React, { useRef, useState } from 'react';

/**
 * 96×96 project logo upload zone. With src: image fills, object-fit:
 * cover, hover reveals a Remove affordance in the top-right corner.
 * Without src: dashed border + image-plus icon + "LOGO" eyebrow.
 *
 * Supports both click-to-upload and drag-and-drop.
 *
 * Props:
 *   src         — current logo URL (null when empty)
 *   onUpload(file)
 *   onRemove()
 *   uploading   — boolean; shows "..." overlay
 */
export default function LogoUpload({ src, onUpload, onRemove, uploading = false, size = 96 }) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const compact = size < 96;
  const radius = compact ? 8 : 12;

  const trigger = () => inputRef.current && inputRef.current.click();

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB.');
      return;
    }
    onUpload && onUpload(file);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div
        onClick={src ? undefined : trigger}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: radius,
          border: src ? '1px solid var(--border-subtle)' : `${dragOver ? '2' : '1'}px dashed ${dragOver ? 'var(--accent)' : 'var(--border-default)'}`,
          background: 'var(--bg-surface-2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: src ? 'default' : 'pointer',
          overflow: 'hidden',
          transition: 'border-color .12s, background .12s',
        }}
      >
        {src ? (
          <>
            <img
              src={src}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {hover && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove && onRemove(); }}
                title="Remove logo"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 22, height: 22, borderRadius: '50%',
                  border: 'none', background: 'rgba(0, 0, 0, 0.55)',
                  color: '#fff', fontSize: 15.5, lineHeight: 1, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >×</button>
            )}
          </>
        ) : (
          <>
            <svg width={compact ? 16 : 22} height={compact ? 16 : 22} viewBox="0 0 24 24" fill="none" stroke="var(--text-4)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="11" r="2" />
              <path d="m21 19-5-5-5 5" />
              <path d="M14 9h.01" />
            </svg>
            <span style={{ fontSize: compact ? 9 : 10.5, color: 'var(--text-4)', marginTop: compact ? 3 : 4, fontWeight: 600, letterSpacing: '0.06em' }}>
              LOGO
            </span>
          </>
        )}
        {uploading && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: radius,
            background: 'color-mix(in oklch, var(--bg-surface) 70%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12.5, color: 'var(--text-3)',
          }}>...</div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFile(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
      </div>
      <button
        type="button"
        onClick={trigger}
        style={{
          fontSize: compact ? 10.5 : 12.5,
          color: 'var(--text-3)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        {src ? (compact ? 'Replace' : 'Change logo') : 'Upload logo'}
      </button>
    </div>
  );
}
